import React, { useEffect, useState, useMemo, useContext } from 'react';
import { Col, Container, Row, Button } from 'reactstrap';
import CommonTableIndexWise from '../../../../Components/Common/CommonTableIndexWise';
import { useGlobalStudy } from 'context/StudyContext';
import { formatDate, GetUserData } from 'common/service/CommonService';
import CommonSearch from 'Components/Common/CommonSearch';
import StatCard from '../../../../Components/Common/StatCard';
import XLSX from 'xlsx-js-style';
import { useReportsSummaryService } from 'modules/Pharmacy/Reports/api/useReportsSummary';
import { BreadcrumbContext } from 'Layouts/Header';
import { REPORTS_PATHS } from 'Routes/paths';

const StockExpiryReport = () => {
    const breadcrumbContext = useContext(BreadcrumbContext);

    useEffect(() => {
        breadcrumbContext?.setBreadcrumbItems?.([
            { text: "Pharmacy Reports", link: REPORTS_PATHS.SUMMARY },
            { text: "Expiring Soon" }
        ]);
        return () => breadcrumbContext?.setBreadcrumbItems?.([]);
    }, []);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [searchText, setSearchText] = useState<string>(" ");

    const { selectedStudy: globalStudy } = useGlobalStudy();
    const [selectedStudy, setSelectedStudy] = useState<string>("");

    useEffect(() => {
        if (globalStudy?.studyid) {
            setSelectedStudy(String(globalStudy.studyid));
        }
        else if (globalStudy === null) {
            setSelectedStudy("");
        }
    }, [globalStudy]);

    const { SelectIpReportsQuery } = useReportsSummaryService();
    const activeStudyId = selectedStudy || globalStudy?.studyid || null;
    const ipSelectQuery = SelectIpReportsQuery(
        {
            studyid: activeStudyId ? Number(activeStudyId) : null,
            pageNumber: currentPage,
            pageSize: itemsPerPage,
            searchText: searchText,
        });
    const inventoryList = ipSelectQuery.data?.data || [];

    const totalRecords = inventoryList.length > 0 ? inventoryList[0].totalcount : 0;

    // Dynamic expiry stats computation from response payload
    const stats = useMemo(() => {
        if (inventoryList.length === 0) {
            return {
                expiringCount: 0,
                affectedQty: 0, // Currently not in API payload, maybe zero it or pull it later
                expiredCount: 0,
                within7DaysCount: 0
            };
        }

        const firstItem = inventoryList[0];

        return {
            expiringCount: firstItem.expiringwithin30days || 0,
            affectedQty: 0,
            expiredCount: firstItem.alreadyexpired || 0,
            within7DaysCount: firstItem.expiringwithin7days || 0
        };
    }, [inventoryList]);

    const columns: any = [
        {
            key: 'studycode',
            label: 'Study Code',
            render: (val: any) => <span className="text-dark fw-semibold fs-13">{val || '-'}</span>
        },
        {
            key: 'ipcategory',
            label: 'IP Category',
            render: (val: any) => {
                const isTestProduct =
                    val?.toLowerCase() === 'test product';

                return (
                    <span
                        className="px-2 py-1 fs-11 rounded fw-semibold"
                        style={{
                            background: isTestProduct
                                ? 'rgba(124, 58, 237, 0.10)'
                                : 'rgba(15, 118, 110, 0.10)',

                            color: isTestProduct
                                ? '#7c3aed'
                                : '#0f766e',

                            border: isTestProduct
                                ? '1px solid rgba(124, 58, 237, 0.18)'
                                : '1px solid rgba(15, 118, 110, 0.18)',

                            letterSpacing: '0.2px'
                        }}
                    >
                        {val || '-'}
                    </span>
                );
            }
        },
        {
            key: 'productname',
            label: 'Product',
            render: (val: any) => <span className="text-dark fw-bold fs-13">{val || '-'}</span>
        },
        {
            key: 'batchlotno',
            label: 'Batch/Lot No',
            render: (val: any) => <span className="badge bg-light text-muted border px-2 py-1 fs-12">{val || '-'}</span>
        },
        {
            key: 'manufacturername',
            label: 'Manufacturer Name',
            render: (val: any) => <span className="text-muted fs-13">{val || '-'}</span>
        },
        {
            key: 'manufacturingdate',
            label: 'Manufacturing Date',
            render: (val: any) => <span className="text-muted fs-13">{formatDate(val)}</span>
        },
        {
            key: 'expirydate',
            label: 'Expiry Date',
            render: (val: any) => (
                <span className="text-muted fs-13 fw-medium">
                    <i className="ri-calendar-event-line me-1 text-danger"></i> {formatDate(val)}
                </span>
            )
        },
        {
            key: 'daysleft',
            label: 'Days Left',
            render: (val: any) => {
                if (val === null || val === undefined || val === '') return '-';

                const numVal = Number(val);
                let badgeColor = 'success';
                let displayText = `${numVal} Days`;

                if (numVal <= 0) {
                    badgeColor = 'danger';
                    displayText = 'Expired';
                } else if (numVal <= 7) {
                    badgeColor = 'danger';
                } else if (numVal <= 30) {
                    badgeColor = 'warning';
                }

                return (
                    <span className={`badge bg-${badgeColor}-subtle text-${badgeColor} border border-${badgeColor}-subtle px-2 py-1 fs-11 rounded fw-bold`}>
                        {displayText}
                    </span>
                );
            }
        },
        {
            key: 'currentquantity',
            label: 'Current Quantity',
            render: (val: any) => (
                <span className="text-dark fw-bold d-flex align-items-center gap-1 fs-13">
                    <i className="ri-checkbox-blank-circle-fill fs-10 text-success"></i> {val ?? 0}
                </span>
            )
        }
    ];

    const onSearch = (text: string | null) => {
        setSearchText(text ?? " ");
        setCurrentPage(1);
    };

    const handleExport = () => {
        const fileName = `Expiring_Stock_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;

        const headerRow = [
            "Study Code",
            "IP Category",
            "Product Name",
            "Batch / Lot No",
            "Manufacturer Name",
            "Manufacturing Date",
            "Expiry Date",
            "Days Left",
            "Current Quntity"
        ];

        const dataRows = inventoryList.map((item: any) => {
            let displayText = 'Expired';

            if (item.daysleft !== null && item.daysleft !== undefined && item.daysleft !== '') {
                const daysNum = Number(item.daysleft);
                if (daysNum > 0) {
                    displayText = `${daysNum} Days`;
                } else if (daysNum <= 0) {
                    displayText = 'Expired';
                }
            }

            return [
                item.studycode || "-",
                item.ipcategory || "-",
                item.productname || "-",
                item.batchlotno || "-",
                item.manufacturername || "-",
                formatDate(item.manufacturingdate) || "-",
                formatDate(item.expirydate) || "-",
                displayText,
                Number(item.currentquantity) ?? 0
            ];
        });

        const user = GetUserData();

        const titleRow = ["EXPIRING STOCK REPORT", "", "", "", "", "", "", "📅", ""];
        const subtitle = ["Identify chemicals and reagents nearing their expiration dates", "", "", "", "", "", "", "", ""];
        const generatedRow = [`Generated On: ${new Date().toLocaleString()} | Generated By: ${user?.userdisplayname || 'System User'}`, "", "", "", "", "", "", "", ""];

        const allRows = [
            [""], // Row 1: Spacer
            titleRow, // Row 2
            subtitle, // Row 3
            generatedRow, // Row 4
            [""], // Row 5: Spacer between generated metadata and table headers
            headerRow, // Row 6
            ...dataRows,
            [""], // Spacer Row before warning note
            [`🛡️ Note: Figures are system generated. Please verify before taking action.`, "", "", "", "", "", "", "", ""] // Footer warning row
        ];

        const ws = XLSX.utils.aoa_to_sheet(allRows);

        // Core Premium Palette Styles (Segoe UI Font Family)
        const titleStyle = {
            font: { name: "Segoe UI", sz: 18, bold: true, color: { rgb: "064E3B" } }, // Dark green
            alignment: { vertical: "center", horizontal: "center" },
            fill: { fgColor: { rgb: "FFFFFF" } }
        };

        const subtitleStyle = {
            font: { name: "Segoe UI", sz: 10, italic: true, color: { rgb: "047857" } }, // Medium green
            alignment: { vertical: "center", horizontal: "center" },
            fill: { fgColor: { rgb: "FFFFFF" } }
        };

        const metaStyle = {
            font: { name: "Segoe UI", sz: 9.5, color: { rgb: "4B5563" } }, // Cool gray
            alignment: { vertical: "center", horizontal: "center" }, // Center aligned!
            fill: { fgColor: { rgb: "FFFFFF" } }
        };

        const iconStyle = {
            font: { name: "Segoe UI Emoji", sz: 40 }, // Segoe UI Emoji renders calendar 📅 beautifully
            alignment: { vertical: "center", horizontal: "center" },
            fill: { fgColor: { rgb: "EBF7EE" } } // Premium mint-green card background
        };

        const headerStyle = {
            font: { name: "Segoe UI", sz: 10, bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "064E3B" } }, // Deep forest green header
            alignment: { vertical: "center", horizontal: "center" },
            border: {
                top: { style: "thin", color: { rgb: "043E2B" } },
                bottom: { style: "thin", color: { rgb: "043E2B" } },
                left: { style: "thin", color: { rgb: "043E2B" } },
                right: { style: "thin", color: { rgb: "043E2B" } }
            }
        };

        const cellBorder = {
            top: { style: "thin", color: { rgb: "E2E8F0" } },
            bottom: { style: "thin", color: { rgb: "E2E8F0" } },
            left: { style: "thin", color: { rgb: "E2E8F0" } },
            right: { style: "thin", color: { rgb: "E2E8F0" } }
        };

        const textStyle = {
            font: { name: "Segoe UI", sz: 10, color: { rgb: "1F2937" } },
            alignment: { vertical: "center", horizontal: "center" },
            border: cellBorder
        };

        const numberStyle = {
            font: { name: "Segoe UI", sz: 10, bold: true, color: { rgb: "1F2937" } },
            alignment: { vertical: "center", horizontal: "center" },
            border: cellBorder
        };

        const dateStyle = {
            font: { name: "Segoe UI", sz: 10, color: { rgb: "1F2937" } },
            alignment: { vertical: "center", horizontal: "center" },
            border: cellBorder
        };

        const getIpCategoryStyle = (val: string, rowFill: any) => {
            const isTestProduct = val?.toLowerCase() === 'test product';
            return {
                font: { name: "Segoe UI", sz: 10, bold: true, color: { rgb: isTestProduct ? "7C3AED" : "0F766E" } },
                fill: rowFill,
                alignment: { vertical: "center", horizontal: "center" },
                border: cellBorder
            };
        };

        const footerStyle = {
            font: { name: "Segoe UI", sz: 10, italic: true, bold: true, color: { rgb: "064E3B" } }, // Dark green
            alignment: { vertical: "center", horizontal: "center" },
            fill: { fgColor: { rgb: "EBF7EE" } } // Super light mint green
        };

        // Custom Merge Rules
        ws["!merges"] = [
            // Title (A2:G2) - extended to column G
            { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } },
            // Subtitle (A3:G3) - extended to column G
            { s: { r: 2, c: 0 }, e: { r: 2, c: 6 } },
            // Meta info (A4:G4) - extended to column G
            { s: { r: 3, c: 0 }, e: { r: 3, c: 6 } },
            // Icon (H2:I4) - moved to columns H & I (index 7 and 8)
            { s: { r: 1, c: 7 }, e: { r: 3, c: 8 } },
            // Warning note at the end (A_last:I_last) - extended to column I
            { s: { r: 6 + dataRows.length + 1, c: 0 }, e: { r: 6 + dataRows.length + 1, c: 8 } }
        ];

        const getIconBorder = (r: number, c: number) => {
            return {
                top: r === 1 ? { style: "thin", color: { rgb: "A5D6A7" } } : undefined,
                bottom: r === 3 ? { style: "thin", color: { rgb: "A5D6A7" } } : undefined,
                left: c === 7 ? { style: "thin", color: { rgb: "A5D6A7" } } : undefined,
                right: c === 8 ? { style: "thin", color: { rgb: "A5D6A7" } } : undefined
            };
        };

        const getFooterBorder = (c: number) => {
            const border: any = {
                top: { style: "thin", color: { rgb: "A5D6A7" } },
                bottom: { style: "thin", color: { rgb: "A5D6A7" } }
            };
            if (c === 0) border.left = { style: "thin", color: { rgb: "A5D6A7" } };
            if (c === 8) border.right = { style: "thin", color: { rgb: "A5D6A7" } };
            return border;
        };

        const range = XLSX.utils.decode_range(ws['!ref'] || "");
        for (let r = range.s.r; r <= range.e.r; ++r) {
            const isAlternate = r % 2 === 0;
            const rowFill = isAlternate ? { fgColor: { rgb: "F5F8F6" } } : { fgColor: { rgb: "FFFFFF" } };

            for (let c = range.s.c; c <= range.e.c; ++c) {
                const cellRef = XLSX.utils.encode_cell({ r, c });
                if (!ws[cellRef]) continue;

                if (r === 0) {
                    ws[cellRef].s = {
                        fill: { fgColor: { rgb: "FFFFFF" } }
                    };
                } else if (r === 1) {
                    if (c <= 6) {
                        ws[cellRef].s = titleStyle;
                    } else {
                        ws[cellRef].s = { ...iconStyle, border: getIconBorder(r, c) };
                    }
                } else if (r === 2) {
                    if (c <= 6) {
                        ws[cellRef].s = subtitleStyle;
                    } else {
                        ws[cellRef].s = { ...iconStyle, border: getIconBorder(r, c) };
                    }
                } else if (r === 3) {
                    if (c <= 6) {
                        ws[cellRef].s = metaStyle;
                    } else {
                        ws[cellRef].s = { ...iconStyle, border: getIconBorder(r, c) };
                    }
                } else if (r === 4) {
                    // Spacer row
                    ws[cellRef].s = {
                        fill: { fgColor: { rgb: "FFFFFF" } }
                    };
                } else if (r === 5) {
                    ws[cellRef].s = headerStyle;
                } else if (r > 5 && r < 6 + dataRows.length) {
                    // Data Rows
                    if (c === 1) {
                        // IP Category - colored badge
                        const ipVal = ws[cellRef].v?.toString() || '';
                        ws[cellRef].s = getIpCategoryStyle(ipVal, rowFill);
                    } else if (c === 5 || c === 6) {
                        ws[cellRef].s = {
                            ...dateStyle,
                            fill: rowFill
                        };
                    } else if (c === 7) {
                        const daysVal = ws[cellRef].v?.toString() || '';
                        let colorRgb = "059669"; // Success
                        if (daysVal.includes("Expired") || daysVal.startsWith("0 ") || daysVal.startsWith("1 ") || daysVal.startsWith("2 ") || daysVal.startsWith("3 ") || daysVal.startsWith("4 ") || daysVal.startsWith("5 ") || daysVal.startsWith("6 ") || daysVal.startsWith("7 ")) {
                            colorRgb = "DC2626"; // Danger
                        } else {
                            const daysNum = parseInt(daysVal);
                            if (!isNaN(daysNum) && daysNum <= 30) {
                                colorRgb = "D97706"; // Warning
                            }
                        }

                        ws[cellRef].s = {
                            font: { fontName: "Segoe UI", sz: 10, bold: true, color: { rgb: colorRgb } },
                            alignment: { vertical: "center", horizontal: "center" },
                            border: cellBorder,
                            fill: rowFill
                        };
                    } else if (c === 8) {
                        ws[cellRef].s = {
                            ...numberStyle,
                            fill: rowFill
                        };
                    } else {
                        ws[cellRef].s = {
                            ...textStyle,
                            fill: rowFill
                        };
                    }
                } else if (r === 6 + dataRows.length) {
                    // Spacer before footer
                    ws[cellRef].s = {
                        fill: { fgColor: { rgb: "FFFFFF" } }
                    };
                } else if (r === 6 + dataRows.length + 1) {
                    // Footer warning row
                    ws[cellRef].s = {
                        ...footerStyle,
                        border: getFooterBorder(c)
                    };
                }
            }
        }

        // Apply column widths
        ws['!cols'] = [
            { wch: 14 },
            { wch: 18 },
            { wch: 18 },
            { wch: 16 },
            { wch: 20 },
            { wch: 20 },
            { wch: 15 },
            { wch: 12 },
            { wch: 16 }
        ];

        // Row Heights
        ws['!rows'] = [
            { hpt: 20 }, // Spacer Row 1
            { hpt: 30 }, // Title Row 2
            { hpt: 22 }, // Subtitle Row 3
            { hpt: 22 }, // Generated Row 4
            { hpt: 20 }, // Spacer Row 5
            { hpt: 26 }  // Header Row 6
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Expiring Stock");
        XLSX.writeFile(wb, fileName);
    };

    return (
        <div className="page-content">
            <Container fluid className="px-4">
                <div className="bg-white p-4 rounded shadow-sm mb-4 border border-light">
                    {/* Summary Cards */}
                    <Row className="mb-4 g-3 px-1">
                        <Col lg={4} md={6}>
                            <StatCard
                                title="EXPIRING SOON"
                                value={stats.expiringCount}
                                subtext="within next 30 days"
                                icon={<i className="ri-alarm-warning-line" />}
                                bgColor="#fef2f2"
                                iconColor="#dc2626"
                                trendType="negative"
                                subtextIcon="ri-alarm-warning-line"
                            />
                        </Col>
                        <Col lg={4} md={6}>
                            <StatCard
                                title="EXPIRED"
                                value={stats.expiredCount}
                                subtext="items already expired"
                                icon={<i className="ri-error-warning-line" />}
                                bgColor="#fffbeb"
                                iconColor="#d97706"
                                trendType="negative"
                                subtextIcon="ri-error-warning-line"
                            />
                        </Col>
                        <Col lg={4} md={6}>
                            <StatCard
                                title="WITHIN 7 DAYS"
                                value={stats.within7DaysCount}
                                subtext="urgent attention needed"
                                icon={<i className="ri-time-line" />}
                                bgColor="#eff6ff"
                                iconColor="#2563eb"
                                trendType="info"
                                subtextIcon="ri-time-line"
                            />
                        </Col>
                    </Row>

                    {/* Table Section Header */}
                    <div className="d-flex justify-content-between align-items-center mb-4 mt-5">
                        <div>
                            <h5 className="mb-1 text-dark fw-bold fs-16">Expiring Stock List</h5>
                            <p className="text-muted mb-0 fs-13">Showing {totalRecords} records</p>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <CommonSearch onSearch={onSearch} searchText={searchText.trim()} />
                            <Button
                                color="light"
                                className="d-flex align-items-center gap-2 text-dark fs-13 border border-light-subtle px-3 py-2"
                                style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}
                                onClick={handleExport}
                            >
                                <i className="ri-file-excel-2-line text-success fs-15"></i> Export Excel
                            </Button>
                        </div>
                    </div>

                    {/* Common Table */}
                    <div className="border rounded border-light overflow-hidden">
                        <CommonTableIndexWise
                            columns={columns}
                            data={inventoryList}
                            loading={ipSelectQuery.isLoading || ipSelectQuery.isFetching}
                            pageSize={itemsPerPage}
                            currentPage={currentPage}
                            totalRecords={totalRecords}
                            onPageChange={setCurrentPage}
                            hideAction={true}
                        />
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default StockExpiryReport;
